import { Button, Modal, Select, Spin } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { fetchSubscription, updateSubscriptionInstallments, updateSubscriptionAnticipation, cancelSubscription, reactivateSubscription } from '../apis/subscriptionsApi';
import { loadWompiScript, showTokenizeModal } from './paymentIntegrations/Wompi';
import { openNotification } from './notifications';
import utc from 'dayjs/plugin/utc';
import LAST_PAYMENT_STATUS from '../enums/LastPaymentStatus';
import Features from '../enums/features';
import { checkAutoPermissions } from '../apis/featureApi';
import { Link } from 'react-router-dom';

dayjs.extend(utc);

const { Option } = Select;

const Subscription = ({ userId }) => {
  const { authenticatedUser } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [savings, setSavings] = useState(null);
  const [allowUpdate, setAllowUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCancelSubcriptionEnabled, setIsCancelSubcriptionEnabled] = useState(false);
  const [isCancelWithoutLastPaymentEnabled, setIsCancelWithoutLastPaymentEnabled] = useState(false);
  const [isCancelWithoutPaymentModalVisible, setIsCancelWithoutPaymentModalVisible] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  const loadSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchSubscription(userId);
      if (response.status === 200) {
        const { subscription, savings } = response.data;
        setSubscription(subscription);
        setSavings(savings);
      } else {
        openNotification('error', 'Error al cargar la subscripción, intenta más tarde');
        setSubscription(null);
        setSavings(null);
      }
    } catch (error) {
      console.error('Subscription - loadSubscription, Error fetching subscription:', error);
      openNotification('error', 'Error al cargar la subscripción, contacta a soporte');
      setSubscription(null);
      setSavings(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    const checkFeatures = async () => {
      setLoading(true);
      const isAuthorized = await checkAutoPermissions(Features.SEE_OTHERS_SUBSCRIPTION, userId);
      if (!isAuthorized) {
          const features = JSON.parse(localStorage.getItem('user_features') || '{}');
          setIsAuthorized(features[Features.SEE_OTHERS_SUBSCRIPTION] || false);
      }else{
          setIsAuthorized(true);
      }
      try{
        const features = JSON.parse(localStorage.getItem('user_features') || '{}');
        setIsCancelSubcriptionEnabled(features[Features.CANCEL_SUBSCRIPTION] || false);
        setIsCancelWithoutLastPaymentEnabled(features[Features.CANCEL_SUBSCRIPTION_WITHOUT_LAST_PAYMENT] || false);
      } catch (err) {
        openNotification('error', 'Error al verificar permisos para la subscripción, contacta a soporte');
      } finally {
        setLoading(false);
      }
    }
    checkFeatures();
  }, [userId]);

  useEffect(() => {
    setAllowUpdate(String(userId) === String(authenticatedUser));
  }, [userId, authenticatedUser]);

  if (!isAuthorized) {
    return null
  }

  const onUpdateInstallments = async (subscriptionId, value) => {
    const result = await updateSubscriptionInstallments(subscriptionId, value);
    if( !result.success ) {
        openNotification(
            "error",
            "Error actualizando las cuotas, intenta más tarde"
        );
        return;
    }
    setSubscription(prev => ({ ...prev, installments: value }));
    openNotification('success', 'Cuotas actualizadas con exito!');
  }

  const onUpdateAnticipation = async (subscriptionId, value) => {
    const result = await updateSubscriptionAnticipation(subscriptionId, value);
    if( !result.success ) {
        openNotification(
            "error",
            "Error actualizando la anticipación, intenta más tarde"
        );
        return;
    }
    setSubscription(prev => ({ ...prev, days_advance_payment: value }));
    openNotification('success', 'Anticipación actualizada con exito!');
  }

  const handleReactivateSubscription = async (subscriptionId) => {
    try {
      setCancelLoading(true);
      await reactivateSubscription(subscriptionId);
      openNotification(
        "success",
        "Subscripción reactivada con éxito."
      );
      await loadSubscription();
    } catch (error) {
      console.error('Subscriptions - reactivateSubscription, Error reactivating subscription:', error);
      openNotification(
        "error",
        "Error reactivando la subscripción. Contacta a soporte"
      );
    } finally {
      setCancelLoading(false);
    }
  }

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      setCancelLoading(true);
      await cancelSubscription(subscriptionId);
      openNotification(
        "success",
        "Se realizará un último cobro para la cancelación. Se envió notificación al usuario. Actualice el PQR"
      );
      await loadSubscription();
    } catch (error) {
      console.error('Subscriptions - cancelSubscription, Error canceling subscription:', error);
      openNotification(
        "error",
        "Error cancelando la subscripción. Contacta a soporte"
      );
    } finally {
      setCancelLoading(false);
    }
  }

  const handleConfirmCancelWithoutPayment = async () => {
    if (!subscription) return;
    try {
      setCancelLoading(true);
      await cancelSubscription(subscription.id, { with_last_payment: false });
      setSubscription(null);
      setIsCancelWithoutPaymentModalVisible(false);
      openNotification(
        "success",
        "Subscripción cancelada sin cobro adicional exitosamente."
      );
      await loadSubscription();
    } catch (error) {
      console.error('Subscriptions - cancelWithoutLastPayment, Error canceling subscription:', error);
      openNotification(
        "error",
        "Error cancelando la subscripción sin último cobro. Contacta a soporte"
      );
    } finally {
      setCancelLoading(false);
    }
  }

  const handleUpdateCard = async () => {
    try {
      await loadWompiScript();
      await showTokenizeModal({
        setLoading,
        onSuccess: async () => {
          const response = await fetchSubscription(userId);
          if (response.status === 200) {
            setSubscription(response.data.subscription);
            setSavings(response.data.savings);
          }
        }
      });
    } catch (err) {
      console.error('Error opening tokenize modal:', err);
      openNotification('error', 'Error abriendo pasarela de pago');
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-3">
        <Spin size="large" />
      </div>
    );
  }

  if (!subscription && !savings) return null;

  return (
    <div className="py-3 col-11">
      <h3 className="mb-3">Subscripción:</h3>
      <div className="themed-block text-center text-md-left d-md-flex p-3 mx-auto mb-3">
        <div className="d-flex flex-wrap justify-content-between w-100">
          {!subscription ? (

            <div className="col-12 d-flex flex-column text-center m-auto">
              { savings.savings > 0 &&  (
                <>
                <p className="font-weight-bold mb-1">Podrías haber ahorrado</p>
                <p>
                  {(savings.savings / 100).toLocaleString('es-CL', {
                  style: 'currency',
                  currency: 'CLP',
                  minimumFractionDigits: 0
                  })}
                </p>
                </>
              )}
              <Link to="/plans">
                <Button className='btn mt-2' type="primary">
                    Ver Subscripciones
                </Button>
            </Link>
            </div>
          ) :
          (
            <div className="row">
              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Plan</p>
                <span>{subscription.plan_name}</span>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Valor</p>
                <span>
                  {(subscription.amount_in_cents != null
                  ? (subscription.amount_in_cents / 100).toLocaleString('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                    })
                  : ''
                  )}
                </span>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Cuotas</p>
                <Select
                  value={subscription.installments || undefined}
                  style={{ minWidth: 80, margin: '0 auto' }}
                  onChange={val => onUpdateInstallments(subscription.id, val)}
                  disabled={!allowUpdate}
                >
                  {[...Array(12)].map((_, i) => (
                  <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </div>

              <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                <p className="font-weight-bold mb-1">Anticipación <small>(días)</small></p>
                <Select
                  value={subscription.days_advance_payment || undefined}
                  style={{ minWidth: 80, margin: '0 auto' }}
                  onChange={val => onUpdateAnticipation(subscription.id, val)}
                  disabled={!allowUpdate}
                >
                  {[...Array(7)].map((_, i) => (
                  <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </div>

              { savings.savings > 0 &&  (
                <div className="col-12 col-md-6 col-xl-3 d-flex flex-column text-center my-2">
                  <p className="font-weight-bold mb-1">Has ahorrado</p>
                  <p>
                    {(savings.savings / 100).toLocaleString('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0
                    })}
                  </p>
                </div>
              )}

              {(!subscription.last_payment_status && (isCancelSubcriptionEnabled || isCancelWithoutLastPaymentEnabled)) && (
                <div className="col-12 d-flex justify-content-center align-items-center gap-2 mt-3 flex-wrap">
                  {isCancelSubcriptionEnabled && (
                    <button
                      className="btn btn-danger m-1"
                      disabled={cancelLoading}
                      onClick={() => handleCancelSubscription(subscription.id)}
                    >
                      {isCancelWithoutLastPaymentEnabled ? "Cancelar con último cobro" : "Cancelar"}
                    </button>
                  )}
                  {isCancelWithoutLastPaymentEnabled && (
                    <button
                      className="btn btn-outline-danger m-1"
                      disabled={cancelLoading}
                      onClick={() => setIsCancelWithoutPaymentModalVisible(true)}
                    >
                      Cancelar sin último cobro
                    </button>
                  )}
                </div>
              )}

              {subscription.last_payment_status && subscription.last_payment_status !== LAST_PAYMENT_STATUS.DONE && (
                <div className="col-12 d-flex flex-column text-center mt-3">
                  <p className="font-weight-bold mb-1">Cancelación</p>
                  <span>Pendiente último cobro</span>
                  <div className="d-flex justify-content-center align-items-center gap-2 mt-2 flex-wrap">
                    <button
                      className="btn btn-secondary m-1"
                      disabled={cancelLoading}
                      onClick={() => handleReactivateSubscription(subscription.id)}
                    >
                      Deshacer cancelación
                    </button>
                    {isCancelWithoutLastPaymentEnabled && (
                      <button
                        className="btn btn-outline-danger m-1"
                        disabled={cancelLoading}
                        onClick={() => setIsCancelWithoutPaymentModalVisible(true)}
                      >
                        Cancelar sin último cobro
                      </button>
                    )}
                  </div>
                </div>
              )}

              {allowUpdate && (
                <div className="col-12 text-center mt-3">
                  <button
                    className="btn themed-btn"
                    onClick={handleUpdateCard}
                  >
                    Cambiar Tarjeta de Pago
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        title="Cancelar suscripción sin último cobro"
        open={isCancelWithoutPaymentModalVisible}
        onOk={handleConfirmCancelWithoutPayment}
        onCancel={() => setIsCancelWithoutPaymentModalVisible(false)}
        okText="Confirmar cancelación"
        cancelText="Volver"
        okButtonProps={{ danger: true, loading: cancelLoading }}
        cancelButtonProps={{ disabled: cancelLoading }}
      >
        <p>¿Estás segura de que deseas cancelar la suscripción inmediatamente <strong>sin realizar el último cobro</strong>?</p>
        <p className="text-muted mb-0">Esta acción dará de baja la suscripción de forma definitiva sin cobros futuros.</p>
      </Modal>
    </div>
  );
};

export default Subscription;